<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Trash } from "lucide-vue-next";
  import { Card, CardHeader, CardContent } from "@/components/ui/card";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Label } from "@/components/ui/label";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useWeightingsStore } from "@stores/weightings";

  const props = defineProps({ index: { type: Number, required: true } });

  const weightingsStore = useWeightingsStore();
  const { selectedWeighting, quotaQuestionOptions, saveAttempted } = storeToRefs(weightingsStore);
  const { updateAnswersQuotas, quotaUnitOptions, removeQuota } = weightingsStore;

  const questionOptions = computed(() =>
    quotaQuestionOptions.value.map((opt) => ({ ...opt, value: String(opt.value) })),
  );

  const handleQuestionChange = (val) => {
    const questionId = Number(val);
    selectedWeighting.value.quotas[props.index].question_id = questionId;
    updateAnswersQuotas(questionId, props.index);
  };

  function changeQuotaTarget(quotaIndex, answerIndex, e) {
    selectedWeighting.value.quotas[quotaIndex].answers[answerIndex].target = e;
  }

  const quota = computed(() => selectedWeighting.value.quotas[props.index]);

  const isValidPercentage = computed(() => {
    if (quota.value.quota_unit !== "percentage") return true;
    return quota.value.answers?.reduce((acc, answer) => acc + answer.target, 0) === 100;
  });

  const isValidNumber = computed(() => {
    if (quota.value.quota_unit !== "number") return true;
    return quota.value.answers?.reduce((acc, answer) => acc + answer.target, 0) == selectedWeighting.value.total;
  });
</script>

<template>
  <Card class="shadow-none my-6">
    <CardHeader class="flex flex-row items-center justify-between py-3 px-4">
      <div class="flex items-center gap-2">
        <span class="font-medium">#{{ index + 1 }}</span>
        <Badge
          v-if="saveAttempted && !isValidPercentage"
          variant="destructive">
          Total value must be 100%
        </Badge>
        <Badge
          v-if="saveAttempted && !isValidNumber"
          variant="destructive">
          Total value must be {{ selectedWeighting.total }}
        </Badge>
      </div>
      <ConfirmDialog
        title="Delete quota"
        description="Are you sure you want to delete this quota?"
        confirm-label="Yes"
        cancel-label="No"
        variant="default"
        @confirm="removeQuota(index)">
        <template #trigger>
          <Button
            variant="ghost"
            size="icon">
            <Trash class="h-4 w-4" />
          </Button>
        </template>
      </ConfirmDialog>
    </CardHeader>
    <CardContent class="space-y-4 px-4 pb-4 pt-0">
      <div class="space-y-2">
        <Label>Question</Label>
        <Select
          :model-value="quota.question_id ? String(quota.question_id) : undefined"
          :options="questionOptions"
          filterable
          placeholder="Select a question"
          @update:model-value="handleQuestionChange" />
      </div>
      <div class="space-y-2">
        <Label>Unit</Label>
        <Select
          v-model="quota.quota_unit"
          :options="quotaUnitOptions"
          placeholder="Unit" />
      </div>
      <div
        v-if="quota.question_id"
        class="pt-4 space-y-2">
        <div class="grid grid-cols-[2fr_3fr_auto_1fr] items-center gap-2">
          <p class="text-sm font-medium text-muted-foreground">Answer</p>
          <p class="text-sm font-medium text-muted-foreground">Target</p>
          <div />
          <p class="text-sm font-medium text-muted-foreground text-center">Avg weight</p>
        </div>
        <template v-if="quota">
          <div
            v-for="(answer, key) in quota.answers"
            :key="key"
            class="grid grid-cols-[2fr_3fr_auto_1fr] items-center gap-2">
            <span class="truncate text-sm">{{ answer.name }}</span>
            <Input
              type="number"
              :model-value="answer.target"
              @update:model-value="(val) => changeQuotaTarget(index, key, Number(val) || 0)" />
            <div />
            <span class="text-sm text-center">
              {{ Math.round((answer.avg_weight + Number.EPSILON) * 100) / 100 || 0 }}
            </span>
          </div>
        </template>
      </div>
    </CardContent>
  </Card>
</template>
